import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { TrendingUp, ArrowRight, User } from 'lucide-react'
import { api } from '../lib/api'
import BrandForm from '../components/BrandForm'

export default function Register() {
  const [step, setStep] = useState(1)
  const [accountData, setAccountData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [existingIndustries, setExistingIndustries] = useState<string[]>([])
  const navigate = useNavigate()

  // Fetch existing industries for autocomplete (used in BrandForm)
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await api.get('/brands/industries')
        setExistingIndustries(response.data.industries || [])
      } catch (error) {
        console.error('Failed to fetch industries:', error)
      }
    }
    if (step === 2) {
      fetchIndustries()
    }
  }, [step])

  const handleAccountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountData({
      ...accountData,
      [e.target.name]: e.target.value
    })
  }

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Create user account
      const response = await api.post('/auth/register', {
        ...accountData,
        skipBrand: true // Skip brand creation in the backend
      })

      // Store token and authenticate
      const { token } = response.data
      localStorage.setItem('token', token)
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`

      // Move to step 2
      setStep(2)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create account')
    } finally {
      setLoading(false)
    }
  }

  const handleBrandSuccess = async (brand: any) => {
    // Brand created successfully, activate it and navigate to dashboard
    try {
      await api.post(`/brands/${brand.id}/activate`)

      // Force a page reload to refresh auth context with new brand
      window.location.href = '/dashboard'
    } catch (error) {
      console.error('Failed to activate brand:', error)
      // Still navigate to dashboard even if activation fails
      navigate('/dashboard')
    }
  }

  const handleBack = () => {
    setStep(1)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex items-center justify-center">
          <TrendingUp className="h-12 w-12 text-indigo-600" />
          <span className="ml-2 text-2xl font-bold text-gray-900">BrandIndex AI</span>
        </Link>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {step === 1 ? 'Create your account' : 'Add your brand'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {step === 1 ? (
            <>
              Or{' '}
              <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
                sign in to your account
              </Link>
            </>
          ) : (
            <>
              Step 2 of 2: Tell us about your brand
            </>
          )}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
          {step === 1 ? (
            // Step 1: Account Creation
            <form className="space-y-6" onSubmit={handleCreateAccount}>
              {error && (
                <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={accountData.email}
                  onChange={handleAccountChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={accountData.password}
                  onChange={handleAccountChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First name (optional)
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={accountData.firstName}
                    onChange={handleAccountChange}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last name (optional)
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={accountData.lastName}
                    onChange={handleAccountChange}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating account...' : 'Continue'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </form>
          ) : (
            // Step 2: Brand Creation using BrandForm
            <div>
              <button
                onClick={handleBack}
                className="mb-4 text-sm text-gray-600 hover:text-gray-900 flex items-center"
              >
                ← Back to account info
              </button>

              <BrandForm
                onSuccess={handleBrandSuccess}
                existingIndustries={existingIndustries}
                submitButtonText="Continue with Setup"
                showCancelButton={false}
              />
            </div>
          )}
        </div>

        {step === 1 && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-indigo-50 text-gray-500">What you'll get</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <User className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-gray-900">AI Visibility Tracking</h3>
                  <p className="mt-1 text-xs text-gray-500">Monitor how AI assistants mention your brand</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-gray-900">Real-time Insights</h3>
                  <p className="mt-1 text-xs text-gray-500">Get actionable recommendations to improve</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
